/**
 * IncidentRouter — binds FunnelManager intake submissions to
 * IncidentManager.createIncident.
 *
 * Flow:
 *   1. Caller opens the intake funnel (definition INCIDENT_INTAKE_V1).
 *   2. Caller submitAnswer's each surface as they fill the form.
 *   3. Caller calls `IncidentRouter.completeIntake({funnelId})` instead of
 *      FunnelManager.completeFunnel directly. The router:
 *        - validates the answer set
 *        - calls FunnelManager.completeFunnel (writes submission + audit)
 *        - calls IncidentManager.createIncident with the parsed answers
 *        - returns both the submissionId and the incidentId
 *
 * This wrapper is the seam between the generic funnel substrate and the
 * ALDS-specific incident creation. FunnelManager remains content-neutral;
 * IncidentManager doesn't know about funnels. The router is the only place
 * the two are coupled.
 */

import type { AppContext } from '../app';
import { FunnelManager, type CompleteFunnelResult } from '../funnels/FunnelManager';
import {
  IncidentManager,
  type CreateIncidentResult,
  type GeoPoint,
  type IncidentCategory,
  type IncidentSeverity,
} from './IncidentManager';

// ─────────────────────────────────────────────────────────────────────────────
// FUNNEL DEFINITION
// ─────────────────────────────────────────────────────────────────────────────

export const INCIDENT_INTAKE_FUNNEL_ID = 'incident-intake';
export const INCIDENT_INTAKE_VERSION = '1';

/**
 * The intake funnel definition. Shape is FunnelManager-opaque; the router
 * + UI know how to interpret it.
 *
 * Surfaces are answered in order; each has a stable `id` used as the key in
 * the answers map.
 */
export const INCIDENT_INTAKE_V1 = {
  title: 'New incident report',
  description:
    'Anonymous neighbor-to-neighbor dispatch. Submitted under a fresh per-incident key.',
  surfaces: [
    {
      id: 'category',
      prompt: 'What is happening?',
      type: 'choice',
      options: ['medical', 'safety', 'utility', 'property', 'welfare-check', 'other'],
      required: true,
    },
    {
      id: 'proposedSeverity',
      prompt: 'How urgent (your read)?',
      type: 'choice',
      options: ['life-threat', 'urgent', 'assistance', 'info'],
      required: true,
    },
    {
      id: 'location',
      prompt: 'Where is it?',
      type: 'geo',
      required: false,
    },
    {
      id: 'narrative',
      prompt: 'Anything else dispatchers should know?',
      type: 'text',
      required: false,
    },
  ],
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// ROUTER
// ─────────────────────────────────────────────────────────────────────────────

export interface CompleteIntakeResult {
  submissionId: string;
  incidentId: string;
  threadId: string;
  funnelHash: string;
}

export interface CompleteIntakeOptions {
  /**
   * Optional Phase 11 attestation: caller has pre-generated a membership
   * proof and pre-verified it. Router threads the verified flag + nullifier
   * + impl into the incident_meta record. Invalid proofs are NOT rejected
   * (per design) — they're flagged on the board for dispatcher attention.
   */
  membershipProof?: {
    nullifier: string;
    verified: boolean;
    impl: 'plaintext' | 'semaphore';
  };
}

/**
 * Phase 17: built-in default categories/severities. The router consults
 * the active plugin (if any) at intake time; these are the fallback when
 * no plugin is active.
 */
const DEFAULT_CATEGORIES: ReadonlySet<string> = new Set([
  'medical', 'safety', 'utility', 'property', 'welfare-check', 'other',
]);
const DEFAULT_SEVERITIES: ReadonlySet<string> = new Set([
  'life-threat', 'urgent', 'assistance', 'info',
]);

export class IncidentRouter {
  readonly #ctx: AppContext;
  readonly #funnels: FunnelManager;
  readonly #incidents: IncidentManager;
  #registered = false;

  constructor(ctx: AppContext) {
    this.#ctx = ctx;
    this.#funnels = new FunnelManager(ctx);
    this.#incidents = new IncidentManager(ctx);
  }

  get funnels(): FunnelManager { return this.#funnels; }
  get incidents(): IncidentManager { return this.#incidents; }

  /**
   * Idempotent — registers INCIDENT_INTAKE_V1 in funnel_definitions if not
   * already present at that content-hash.
   */
  async ensureFunnelRegistered(): Promise<void> {
    if (this.#registered) return;
    await this.#funnels.registerDefinition({
      funnelId: INCIDENT_INTAKE_FUNNEL_ID,
      version: INCIDENT_INTAKE_VERSION,
      definition: INCIDENT_INTAKE_V1 as unknown as Record<string, unknown>,
    });
    this.#registered = true;
  }

  /**
   * Phase 17: returns the active plugin's category + severity keys, or the
   * built-in defaults if no plugin is active. Used by the intake UI to
   * render the picker and by `completeIntake` to validate.
   */
  async getActiveCategories(): Promise<{ categories: ReadonlySet<string>; severities: ReadonlySet<string> }> {
    try {
      const { PluginManager } = await import('../plugins/PluginManager');
      const pm = new PluginManager(this.#ctx);
      const manifest = await pm.getEffective();
      const categories = new Set(manifest.categories.map((c) => c.key));
      const severities = manifest.severities !== undefined
        ? new Set(manifest.severities.map((s) => s.key))
        : DEFAULT_SEVERITIES;
      return { categories, severities };
    } catch {
      // Fallback if the plugin layer isn't available for any reason.
      return { categories: DEFAULT_CATEGORIES, severities: DEFAULT_SEVERITIES };
    }
  }

  /**
   * Finalize the intake funnel and create the incident from its answers.
   * Throws if required answers are missing or values are invalid.
   */
  async completeIntake(opts: CompleteIntakeOptions = {}): Promise<CompleteIntakeResult> {
    await this.ensureFunnelRegistered();

    // Snapshot answers via openFunnel (returns the current draft).
    const open = await this.#funnels.openFunnel({ funnelId: INCIDENT_INTAKE_FUNNEL_ID });
    const { categories, severities } = await this.getActiveCategories();
    const validated = validateIntakeAnswers(open.answers, categories, severities);

    // Finalize the funnel submission — writes to own_submissions + audit.
    const submission: CompleteFunnelResult = await this.#funnels.completeFunnel({
      funnelId: INCIDENT_INTAKE_FUNNEL_ID,
    });

    // Create the incident bound to that submission.
    const incident: CreateIncidentResult = await this.#incidents.createIncident({
      category: validated.category,
      proposedSeverity: validated.proposedSeverity,
      submissionId: submission.submissionId,
      ...(validated.narrative !== undefined ? { narrative: validated.narrative } : {}),
      ...(validated.location !== undefined ? { location: validated.location } : {}),
      ...(opts.membershipProof !== undefined ? { membershipProof: opts.membershipProof } : {}),
    });

    return {
      submissionId: submission.submissionId,
      incidentId: incident.incidentId,
      threadId: incident.threadId,
      funnelHash: submission.funnelHash,
    };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// VALIDATION
// ─────────────────────────────────────────────────────────────────────────────

interface ValidatedIntake {
  category: IncidentCategory;
  proposedSeverity: IncidentSeverity;
  narrative?: string;
  location?: GeoPoint;
}

export class IntakeValidationError extends Error {
  constructor(public field: string, message: string) {
    super(`incident-intake invalid: ${field}: ${message}`);
    this.name = 'IntakeValidationError';
  }
}

function validateIntakeAnswers(
  answers: Record<string, unknown>,
  validCategories: ReadonlySet<string> = DEFAULT_CATEGORIES,
  validSeverities: ReadonlySet<string> = DEFAULT_SEVERITIES,
): ValidatedIntake {
  const category = answers.category;
  if (typeof category !== 'string' || !validCategories.has(category)) {
    throw new IntakeValidationError('category', `not a recognized category (got ${JSON.stringify(category)})`);
  }
  const severity = answers.proposedSeverity;
  if (typeof severity !== 'string' || !validSeverities.has(severity)) {
    throw new IntakeValidationError('proposedSeverity', `not a recognized severity (got ${JSON.stringify(severity)})`);
  }
  const result: ValidatedIntake = {
    category: category as IncidentCategory,
    proposedSeverity: severity as IncidentSeverity,
  };
  if (typeof answers.narrative === 'string' && answers.narrative.trim() !== '') {
    result.narrative = answers.narrative.trim();
  }
  if (answers.location !== undefined && answers.location !== null) {
    result.location = validateGeoPoint(answers.location);
  }
  return result;
}

function validateGeoPoint(raw: unknown): GeoPoint {
  if (typeof raw !== 'object' || raw === null) {
    throw new IntakeValidationError('location', 'not an object');
  }
  const r = raw as Record<string, unknown>;
  const lat = r.lat, lng = r.lng, accuracyM = r.accuracyM, fixAt = r.fixAt;
  if (typeof lat !== 'number' || Math.abs(lat) > 90) {
    throw new IntakeValidationError('location.lat', 'expected number in [-90, 90]');
  }
  if (typeof lng !== 'number' || Math.abs(lng) > 180) {
    throw new IntakeValidationError('location.lng', 'expected number in [-180, 180]');
  }
  if (typeof accuracyM !== 'number' || accuracyM < 0) {
    throw new IntakeValidationError('location.accuracyM', 'expected non-negative number');
  }
  if (typeof fixAt !== 'string') {
    throw new IntakeValidationError('location.fixAt', 'expected ISO timestamp string');
  }
  return { lat, lng, accuracyM, fixAt };
}
